import React from "react";
import './Kanvan.scss';
import Status from "../../../../../../components/Atomsv2/Status/Status";
import Text from "../../../../../../components/Atomsv2/Text/Text";
import Avatar from "../../../../../../components/Atomsv2/Avatar/Avatar";
import Tooltip from "../../../../../../components/Atomsv2/Tooltip/Tooltip";
import { formateoBasico } from "../../../../../../utilidades/formateos";
import ProgressBar from "../../../../../../components/Atomsv2/ProgressBar/ProgressBar";

const prioridadColores = { 1: 'error', 2: 'warning', 3: 'success' };
const estatusColores = { 1: 'default', 2: 'warning', 3: 'primary', 4: 'neutral', 5: 'success', 6: 'error' };

const tooltipSubtarea = (sub) => {
    const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    return (
        <div className="subtarea-tooltip-body">
            {sub.DESCRIPCION && <p className="stb-desc">{sub.DESCRIPCION}</p>}
            <div className="stb-grid">
                <span className="stbl">Estatus</span>
                <Status severity={estatusColores[sub.ESTATUS_ID] || 'primary'}>{sub.ESTATUS}</Status>
                <span className="stbl">Prioridad</span>
                <Status severity={prioridadColores[sub.PRIORIDAD_ID] || 'primary'}>{sub.PRIORIDAD}</Status>
                <span className="stbl">Sprint</span>
                <span className="stbv">{sub.SPRINT || '—'}</span>
                <span className="stbl">Entrega</span>
                <span className="stbv">{fmt(sub.FECHA_FIN)}</span>
                <span className="stbl">Asignado</span>
                <span className="stbv">{sub.USUARIO_ASIGNADO_NOMBRE?.trim() || '—'}</span>
            </div>
        </div>
    );
};

const CardKanvan = ({ data, onDragStart, onDragEnd, onClick, isDragging, allTareas, estatusCompletadaId }) => {
    const fechaFin = formateoBasico(data.FECHA_FIN).split('-').reverse().join('/');
    const draggingRef = React.useRef(false);

    const subTareas = React.useMemo(() => {
        try {
            const parsed = JSON.parse(data.SUBTAREAS ?? '[]');
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }, [data.SUBTAREAS]);

    const tareasBloqueantes = React.useMemo(() => {
        try {
            const parsed = JSON.parse(data.TAREAS_BLOQUEANTES ?? '[]');
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }, [data.TAREAS_BLOQUEANTES]);

    // Ocultar badge si la bloqueante ya fue completada (permite re-bloqueo si cambia de estatus)
    const blocantesSinCompletar = React.useMemo(() =>
        tareasBloqueantes.filter(b => {
            if (!estatusCompletadaId) return true;
            const tareaBloc = (allTareas || []).find(t => t.ID === b.ID);
            return !tareaBloc || tareaBloc.ESTATUS_ID !== estatusCompletadaId;
        }),
        [tareasBloqueantes, allTareas, estatusCompletadaId]
    );

    const progreso = React.useMemo(() => {
        const tareaTerminada = data.ESTATUS_ID === 5;
        if (subTareas.length === 0) return tareaTerminada ? 100 : 0;

        // la tarea principal cuenta como un elemento más, así el 100% exige subtareas y tarea completadas
        const total = subTareas.length + 1;
        const terminadas = subTareas.filter(subTarea => subTarea.ESTATUS_ID === 5).length + (tareaTerminada ? 1 : 0);
        return Math.round((terminadas / total) * 100);
    }, [subTareas, data.ESTATUS_ID]);

    const esBloqueada = blocantesSinCompletar.length > 0;

    return (
        <div
            className={`CardKanvan${isDragging ? ' dragging' : ''}`}
            draggable={!esBloqueada}
            onDragStart={!esBloqueada ? () => { draggingRef.current = true; onDragStart(data); } : undefined}
            onDragEnd={() => { onDragEnd(); setTimeout(() => { draggingRef.current = false; }, 0); }}
            onClick={() => { if (!draggingRef.current) onClick?.(data); }}
        >
            <div className="CardKanvan-header">
                <Tooltip content={`Prioridad: ${data.PRIORIDAD}`}>
                    <Status severity={prioridadColores[data.PRIORIDAD_ID] || 'primary'}>
                        {data.PRIORIDAD}
                    </Status>
                </Tooltip>
                <Tooltip content={`Estatus: ${data.ESTATUS}`}>
                    <span className="estatus-label">{data.ESTATUS}</span>
                </Tooltip>
            </div>
            <Tooltip triggerStyle={{ justifyContent: 'flex-start' }} content={`Creado por: ${data.USUARIO_CREACION_NOMBRE?.trim()}`}>
                <Text size='small' weight={600}>{data.TAREA}</Text>
            </Tooltip>
            {data.DESCRIPCION && (
                <Tooltip content="Descripción" triggerStyle={{ justifyContent: 'flex-start' }}>
                    <Text size='extra-small' color='var(--text-color-secondary)'>{data.DESCRIPCION}</Text>
                </Tooltip>
            )}
            <div className="CardKanvan-tags">
                {data.SPRINT && (
                    <Tooltip content={`Sprint: ${data.SPRINT}`}>
                        <span className="tag">{data.SPRINT}</span>
                    </Tooltip>
                )}
                {data.PROYECTO && (
                    <Tooltip content={`Proyecto: ${data.PROYECTO}`}>
                        <span className="tag tag--proyecto">{data.PROYECTO}</span>
                    </Tooltip>
                )}
                {blocantesSinCompletar.length > 0 && (
                    <Tooltip content={`Bloqueada por: ${blocantesSinCompletar.map(b => b.TAREA).join(', ')}`}>
                        <Status severity="neutral">BLOQUEADA POR: {blocantesSinCompletar[0].TAREA}</Status>
                    </Tooltip>
                )}
            </div>
            {subTareas.length > 0 && (
                <div className="CardKanvan-subtareas">
                    <div className="CardKanvan-subtareas-header">
                        <label className="icon" data-icon="account_tree" />
                        <Text size='extra-small' weight={600}>SUBTAREAS</Text>
                        <span className="subtareas-count">{subTareas.length}</span>
                    </div>
                    {subTareas.map(sub => (
                        <Tooltip key={sub.ID} className="tooltip-trigger-proyectos-sub-tareas" content={tooltipSubtarea(sub)}>
                            <div className="CardKanvan-subtarea-row">
                                <label className="icon" data-icon={sub.ESTATUS_ID === 5 ? 'check_box' : 'check_box_outline_blank'} />
                                <Text size='extra-small'>{sub.TAREA}</Text>
                                <Status severity={estatusColores[sub.ESTATUS_ID] || 'primary'}>
                                    {sub.ESTATUS}
                                </Status>
                            </div>
                        </Tooltip>
                    ))}
                </div>
            )}
            <div className="progress-bar-container-proyecto">
                <ProgressBar value={progreso}></ProgressBar>
            </div>
            <div className="CardKanvan-footer">
                {fechaFin && (
                    <Tooltip content={`Fecha de entrega: ${fechaFin}`}>
                        <div className="fecha">
                            <label className="icon" data-icon="today" />
                            <Text size='extra-small'>{fechaFin}</Text>
                        </div>
                    </Tooltip>
                )}
                {data.USUARIO_ASIGNADO_NOMBRE ? (
                    <Tooltip content={`Asignado a: ${data.USUARIO_ASIGNADO_NOMBRE?.trim()}`}>
                        <Avatar src={data.USUARIO_ASIGNADO_FOTO} label={data.USUARIO_ASIGNADO_NOMBRE} />
                    </Tooltip>
                ) : (
                    <Tooltip content="Sin usuario asignado">
                        <span className="sin-asignado">—</span>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};
export default CardKanvan;